<script setup lang="ts">
import OrgChartNode from '@/components/companies/OrgChartNode.vue'
import { layoutGraph, normalizeOrganization, type OrgGraph, type OrgGraphNode } from '@/components/companies/orgGraph'
import { Background } from '@vue-flow/background'
import { type Edge, Handle, Panel, Position, useVueFlow, VueFlow } from '@vue-flow/core'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CompanyOrganization = App.Data.Company.CompanyOrganization
import CompanyHolding = App.Data.Company.CompanyHolding

const props = withDefaults(
  defineProps<{
    organization?: CompanyOrganization
    holdings?: CompanyHolding[]
    graph?: OrgGraph
    interactive?: boolean
    fill?: boolean
    focusNodeId?: string | null
  }>(),
  { interactive: true, holdings: () => [], fill: false, focusNodeId: null },
)

const emit = defineEmits<{
  edit: [id: string]
  delete: [id: string]
  deletePerson: [id: string]
}>()

const { t } = useI18n({ useScope: 'local' })

const { fitView } = useVueFlow()

const isMe = (id: string): boolean => !!props.focusNodeId && props.focusNodeId === id

const isSubject = (id: string): boolean => !props.graph && !!props.organization && props.organization.id === id

const graphData = computed(() => {
  const orgGraph: OrgGraph = props.graph
    ? { nodes: props.graph.nodes, edges: props.graph.edges }
    : props.organization
      ? normalizeOrganization(props.organization, props.holdings)
      : { nodes: [], edges: [] }

  return layoutGraph(orgGraph, { levelLabels: Boolean(props.graph), targetLabel: t('target') })
})

const hoveredNodeId = ref<string | null>(null)

const displayEdges = computed<Edge[]>(() =>
  graphData.value.edges.map((edge) => {
    const isConnected =
      hoveredNodeId.value !== null && (edge.source === hoveredNodeId.value || edge.target === hoveredNodeId.value)

    return { ...edge, class: isConnected ? 'edge-highlighted' : '' }
  }),
)

function fitWholeGraph(): void {
  void fitView({ maxZoom: 1 })
}
</script>

<template>
  <div class="w-full" :class="fill ? 'h-full' : 'border-surface-200 h-100 border'">
    <VueFlow
      :nodes="graphData.nodes"
      :edges="displayEdges"
      :nodes-draggable="false"
      :nodes-connectable="false"
      :elements-selectable="false"
      :zoom-on-double-click="false"
      :zoom-on-scroll="interactive"
      :zoom-on-pinch="interactive"
      :pan-on-drag="interactive"
      :max-zoom="1"
      :fit-view-options="{ maxZoom: 1 }"
      @pane-ready="fitWholeGraph"
      @node-mouse-enter="hoveredNodeId = $event.node.id"
      @node-mouse-leave="hoveredNodeId = null">
      <Background variant="dots" />

      <Panel v-if="interactive" position="top-right">
        <button
          class="bg-surface-0 border-surface-200 hover:bg-surface-50 cursor-pointer rounded border p-2 shadow-sm"
          :title="t('reset')"
          @click="fitWholeGraph">
          <i class="pi pi-expand text-surface-600" />
        </button>
      </Panel>

      <template #node-organization="{ data }: { data: OrgGraphNode }">
        <Handle type="source" :position="Position.Bottom" class="opacity-0" />
        <Handle type="target" :position="Position.Top" class="opacity-0" />
        <OrgChartNode
          :data="data"
          :interactive="interactive"
          :is-me="isMe(data.id)"
          :is-subject="isSubject(data.id)"
          @edit="emit('edit', $event)"
          @delete="emit('delete', $event)"
          @delete-person="emit('deletePerson', $event)" />
      </template>

      <template #node-level-label="{ data }: { data: { text: string } }">
        <div class="section-label section-label--sm font-bold whitespace-nowrap">{{ data.text }}</div>
      </template>
    </VueFlow>
  </div>
</template>

<style scoped>
:deep(.vue-flow__edge-path) {
  stroke: #c3d0d2;
  stroke-width: 1.75;
  transition:
    stroke 150ms ease,
    stroke-width 150ms ease;
}

:deep(.vue-flow__edge.edge-highlighted .vue-flow__edge-path) {
  stroke: var(--color-action);
  stroke-width: 2.5;
}
</style>

<i18n lang="json">
{
  "de": {
    "target": "Ziel",
    "reset": "Zurücksetzen"
  },
  "en": {
    "target": "Target",
    "reset": "Reset"
  }
}
</i18n>
